import { Attraction } from '../models/attraction.model';

/**
 * Formatting helpers for the attractions feature, ported from the Android
 * `AttractionsComponents.kt` (`heroImageUrl`, `formatAttractionPrice`, `formatRating`).
 *
 * No conversion and no invented symbols: prices and ratings are shown exactly as the API reports
 * them, the same way every other client does, so the numbers match across platforms.
 *
 * `attractionsImages` is the exception to the "Attraction-shaped input" rule: it reads the venue
 * payload (`clientData`), mirroring `ShopDetailsResponse.attractionsImages`.
 */

/** The image to lead with, or null when the attraction has none (first non-blank URL). */
export function heroImageUrl(attraction: Attraction | null | undefined): string | null {
  const urls = attraction?.image_urls;
  if (!urls) { return null; }
  const first = urls.find(u => !!u && u.trim().length > 0);
  return first ?? null;
}

/**
 * The venue's attraction photography, cover first, blanks removed.
 *
 * The single source of "which photos represent this venue's attractions": the mosaic banner slices
 * the whole list, while the puck and the classic banner take the first of it.
 *
 * Falls back to the single `attractions_image_url` so a payload cached before
 * `attractions_image_urls` existed — or a backend that only fills the lead photo — still shows a real
 * photo rather than a placeholder. The mosaic renders a one-photo list as a plain image with no
 * seams, and an empty one as its own placeholder.
 *
 * Both branches blank-check: a whitespace-only legacy URL is length-1 but not a real photo, and
 * `AttractionImageComponent` would render `ImageBroken.svg` for it rather than an empty state.
 *
 * `clientData` is the untyped `sessionStorage` blob, hence `any` — see `AttractionsClientFields`.
 */
export function attractionsImages(clientData: any): string[] {
  const list: string[] = (clientData?.attractions_image_urls ?? [])
    .filter((u: string) => !!u && u.trim().length > 0);
  if (list.length) { return list; }
  const single = (clientData?.attractions_image_url ?? '').trim();
  return single ? [single] : [];
}

/**
 * The highlighted attraction's title, or null to fall back to "Explore {city}".
 *
 * Blank-guarded here rather than at the call site so absent, null and '' are one case: a banner titled
 * with an empty string is worse than one titled with the city.
 *
 * `clientData` is the untyped `sessionStorage` blob, hence `any` — see `AttractionsClientFields`.
 */
export function attractionsHighlightTitle(clientData: any): string | null {
  const title = (clientData?.attractions_highlight_title ?? '').trim();
  return title ? title : null;
}

/**
 * How many OTHER attractions there are besides the highlight — the banner's "and N more".
 *
 * Only meaningful alongside `attractionsHighlightTitle()`: the backend guarantees the highlight is
 * inside `attractions_count` whenever it sends a title, so subtracting it cannot disagree with the
 * list. 0 (never negative) means there is nothing more to advertise and the banner shows no chip.
 */
export function attractionsMoreCount(clientData: any): number {
  return Math.max((clientData?.attractions_count ?? 0) - 1, 0);
}

/** Formats a price the way the API reports it. Integer → no decimals; otherwise 2dp. null → null. */
export function formatAttractionPrice(price: number | null | undefined, currency: string | null | undefined): string | null {
  if (price === null || price === undefined) { return null; }
  const amount = Number.isInteger(price) ? String(price) : price.toFixed(2);
  let symbol: string;
  switch ((currency ?? '').toUpperCase()) {
    case 'EUR': symbol = '€'; break;
    case 'USD': symbol = '$'; break;
    case 'GBP': symbol = '£'; break;
    case '': symbol = ''; break;
    default: symbol = `${currency} `; break;
  }
  return `${symbol}${amount}`;
}

/**
 * Formats a rating. The backend rounds to 1dp, so this only formats — it does not re-round.
 * Integer rating renders "5" rather than "5.0". null → "".
 */
export function formatRating(rating: number | null | undefined): string {
  if (rating === null || rating === undefined) { return ''; }
  return Number.isInteger(rating) ? String(rating) : rating.toFixed(1);
}
